import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { RootState } from '../..';
import {
  createCourseTutor,
  deleteCourseTutor,
  getCourseTutorsAsAdmin,
  updateCourseTutor,
  verifyCourseTutorDelete,
} from './adminApi';
import { IBaseUser } from '../../UserSlice';
import { ICourse } from '../CoursesSlice';
import { ISection } from '../SectionSlice';
import { getCourseTutorsByTutor } from './api';
import { ITutorRole } from '../TutorRolesSlice';

export const COURSE_TUTOR_TYPES = {
  PRIMARY: 'PRIMARY',
  SUBSTITUTE: 'SUBSTITUTE',
  TRIAL: 'TRIAL',
} as const;

export const COURSE_TUTOR_STATUSES = {
  ENABLE: 'ENABLE',
  DISABLE: 'DISABLE',
} as const;

export const COURSE_TUTOR_VERIFY_DELETE_TYPES = {
  DELETE: 'DELETE',
  DISABLE: 'DISABLE',
} as const;

export type TCourseTutorType = typeof COURSE_TUTOR_TYPES[keyof typeof COURSE_TUTOR_TYPES];
export type TCourseTutorStatus = typeof COURSE_TUTOR_STATUSES[keyof typeof COURSE_TUTOR_STATUSES];
export type TCourseTutorVerifyDelete = typeof COURSE_TUTOR_VERIFY_DELETE_TYPES[keyof typeof COURSE_TUTOR_VERIFY_DELETE_TYPES];

export interface ICourseTutor {
  _id: string;
  course: ICourse;
  title?: string;
  description?: string;
  status: TCourseTutorStatus;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
  tutor: IBaseUser;
  type?: TCourseTutorType;
  tutorRole?: ITutorRole;
  tutorRateAmount?: number;
  tutorRateCurrency?: string;
}

export interface ICourseTutorDeleteVerificationData {
  proceed: boolean;
  type: TCourseTutorVerifyDelete;
  upcomingClassesCount: number;
  upcomingClasses: ISection[];
  courseTutor: ICourseTutor;
}

export interface IInitialCourseTutorsState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminCourseTutorsData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICourseTutor>;
  };
  adminCourseTutorDeleteVerificationData?: ICourseTutorDeleteVerificationData;
  // User
  courseTutors: Array<ICourseTutor>;
}

const initialState: IInitialCourseTutorsState = {
  status: 'idle',
  // Admin
  adminCourseTutorsData: undefined,
  adminCourseTutorDeleteVerificationData: undefined,
  // User
  courseTutors: [],
};

export const CourseTutorsSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    resetAdminCourseTutorsData: (state) => {
      state.adminCourseTutorsData = undefined;
    },
    resetAdminCourseTutorDeleteVerificationData: (state) => {
      state.adminCourseTutorDeleteVerificationData = undefined;
    },
    resetCourseTutors: (state) => {
      state.courseTutors = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Admin
      // Get course tutors
      .addCase(getCourseTutorsAsAdmin.fulfilled, (state, action) => {
        const result = action.payload;
        const newAdminCourseTutorsData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminCourseTutorsData = newAdminCourseTutorsData;
      })
      // Create course tutor
      .addCase(createCourseTutor.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminCourseTutorsData) {
          const newAdminCourseTutorsData = { ...state.adminCourseTutorsData };

          newAdminCourseTutorsData.list.unshift(result);

          if (newAdminCourseTutorsData.totalDocs) {
            newAdminCourseTutorsData.totalDocs += 1;
          } else {
            newAdminCourseTutorsData.totalDocs = 1;
          }

          state.adminCourseTutorsData = newAdminCourseTutorsData;
        }
      })
      // Delete course tutor
      .addCase(deleteCourseTutor.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminCourseTutorsData) {
          const newAdminCourseTutorsData = { ...state.adminCourseTutorsData };
          newAdminCourseTutorsData.list = newAdminCourseTutorsData.list.filter(
            (item) => item._id !== result._id
          );

          if (newAdminCourseTutorsData.totalDocs) {
            newAdminCourseTutorsData.totalDocs -= 1;
          }

          state.adminCourseTutorsData = newAdminCourseTutorsData;
        }
      })
      // Update course tutor
      .addCase(updateCourseTutor.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminCourseTutorsData) {
          const newAdminCourseTutorsData = { ...state.adminCourseTutorsData };

          const index = newAdminCourseTutorsData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminCourseTutorsData.list[index] = result;
          }

          state.adminCourseTutorsData = newAdminCourseTutorsData;
        }
      })
      .addCase(verifyCourseTutorDelete.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminCourseTutorDeleteVerificationData = result;
      })

      // User
      .addCase(getCourseTutorsByTutor.fulfilled, (state, action) => {
        state.status = 'idle';
        state.courseTutors = action.payload;
      });

    // Matchers
    builder
      .addMatcher(
        isPending(
          // Admin
          getCourseTutorsAsAdmin,
          createCourseTutor,
          deleteCourseTutor,
          updateCourseTutor,
          verifyCourseTutorDelete,
          // User
          getCourseTutorsByTutor
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isFulfilled(
          // Admin
          getCourseTutorsAsAdmin,
          createCourseTutor,
          deleteCourseTutor,
          updateCourseTutor,
          verifyCourseTutorDelete,
          // User
          getCourseTutorsByTutor
        ),
        (state) => {
          state.status = 'idle';
        }
      )
      .addMatcher(
        isRejected(
          // Admin
          getCourseTutorsAsAdmin,
          createCourseTutor,
          deleteCourseTutor,
          updateCourseTutor,
          verifyCourseTutorDelete,
          // User
          getCourseTutorsByTutor
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const {
  resetAdminCourseTutorsData,
  resetAdminCourseTutorDeleteVerificationData,
  resetCourseTutors,
} = CourseTutorsSlice.actions;

export const getCourseTutorsState = (
  state: RootState
): RootState['courseTutors'] => state.courseTutors;

export * from './adminApi';
export default CourseTutorsSlice.reducer;
